import React, { useState, ChangeEvent } from 'react';
import {
  InputLabel,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface TextInputProps {
  name: string;
  id: string;
  value: string;
  label: string;
  type?: string;
  error: boolean;
  minLength?: number;
  maxLength?: number;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  onChange: (value: string) => void;
  handleValid: (isValid: boolean) => void;
  onKeyDown?: () => void;
  errormessage?: any;
}

const TextInput: React.FC<TextInputProps> = ({
  name,
  id,
  value,
  type,
  label,
  error,
  minLength,
  maxLength,
  placeholder,
  onChange,
  handleValid,
  autoComplete = 'off',
  required = false,
  onKeyDown,
  errormessage,
}) => {
  const [touched, setTouched] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (type == 'number') {
      const sanitizedValue = inputValue.replace(/\D/g, '');
      onChange(sanitizedValue);
      setTouched(true);
      validateInput(sanitizedValue);
    } else {
      onChange(inputValue);
      setTouched(true);
      validateInput(inputValue);
    }
  };

  const validateInput = (inputValue: string) => {
    if (minLength !== undefined && maxLength !== undefined) {
      handleValid(
        !(inputValue.length < minLength || inputValue.length > maxLength),
      );
    } else if (minLength !== undefined) {
      handleValid(!(inputValue.length < minLength));
    } else if (maxLength !== undefined) {
      handleValid(!(inputValue.length < 1 || inputValue.length > maxLength));
    } else {
      handleValid(!(inputValue.length < 1));
    }
  };

  return (
    <>
      <InputLabel htmlFor={name}>{label}</InputLabel>
      <TextField
        name={name}
        id={id}
        fullWidth
        variant="outlined"
        type="text"
        placeholder={placeholder || 'Enter text here'}
        value={value}
        onChange={handleChange}
        error={errormessage || (!error && touched)}
        helperText={error && ''}
        required={required}
        autoComplete={autoComplete}
        inputProps={{
          maxLength: maxLength !== undefined ? maxLength : undefined,
          minLength: minLength !== undefined ? minLength : undefined,
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onKeyDown) {
            setTouched(true);
            validateInput(value);
            onKeyDown();
          }
        }}
      />
    </>
  );
};

interface EmailInputProps {
  value: string;
  label?: string;
  id?: string;
  onChange: (value: string) => void;
  onKeyDown?: () => void;
  errormessage?: any;
  error: boolean;
  handleValid: (isValid: boolean) => void;
}

const EmailInput: React.FC<EmailInputProps> = ({
  value,
  label = 'Email',
  id = 'email',
  onChange,
  onKeyDown,
  errormessage,
  error,
  handleValid,
}) => {
  const [isValid, setIsValid] = useState(true);

  const validateEmail = (param: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(param);
    setIsValid(isValid);
    handleValid(isValid);
    return isValid;
  };

  return (
    <>
      <InputLabel htmlFor="email">{label}</InputLabel>
      <TextField
        fullWidth
        id={id}
        margin="none"
        variant="outlined"
        type="email"
        placeholder="Ex. johndoe@gmail.com"
        value={value}
        onChange={(e) => {
          validateEmail(e.target.value);
          onChange(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onKeyDown) {
            validateEmail(value);
            onKeyDown();
          }
        }}
        error={errormessage || !(error || isValid)}
        helperText={(errormessage || !(error || isValid)) && 'Invalid Email'}
      />
    </>
  );
};

interface PasswordInputProps {
  value: string;
  label?: string | null;
  className?: string;
  id?: string;
  errorshow?: boolean;
  onChange: (value: string) => void;
  onKeyDown?: (event: any) => void;
  errormessage?: any;
  error: boolean;
  showPassword?: boolean;
  handleValid: (isValid: boolean) => void;
}

const PasswordInput: React.FC<PasswordInputProps> = ({
  value,
  errorshow = true,
  className = '',
  id = 'password',
  onChange,
  onKeyDown,
  errormessage,
  error,
  handleValid,
  label = 'Password',
}) => {
  const [isValid, setIsValid] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  const validatePassword = (param: string) => {
    const isValid = param?.length > 6;
    setIsValid(isValid);
    handleValid(isValid);
    return isValid;
  };

  return (
    <>
      {label ? <InputLabel htmlFor="password">{label}</InputLabel> : <></>}
      <TextField
        fullWidth
        id={id}
        className={className}
        variant="outlined"
        type={showPassword ? 'text' : 'password'}
        placeholder="Enter password here"
        autoComplete="new-password"
        value={value}
        onChange={(e) => {
          validatePassword(e.target.value);
          onChange(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onKeyDown) {
            validatePassword(value);
            onKeyDown(e);
          }
        }}
        error={errormessage || !(error || isValid)}
        helperText={
          errorshow
            ? (errormessage || !(error || isValid)) &&
              'Password must be at least 7 characters'
            : ''
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={() => setShowPassword(!showPassword)}
                onMouseDown={(e) => e.preventDefault()}
                edge="end"
              >
                {showPassword ? (
                  <VisibilityOff className="iconStyle" />
                ) : (
                  <Visibility className="iconStyle" />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    </>
  );
};

export default PasswordInput;

export { TextInput, EmailInput, PasswordInput };
